class WodyProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.querySelector('[data-product-form]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.formStatus = this.querySelector('[data-form-status]');
    this.price = this.querySelector('[data-product-price]');
    this.comparePrice = this.querySelector('[data-compare-price]');
    this.saving = this.querySelector('[data-saving]');
    this.availability = this.querySelector('[data-availability]');
    this.sticky = this.querySelector('[data-sticky-buy]');
    this.stickyButton = this.querySelector('[data-sticky-submit]');
    this.stickyLabel = this.querySelector('[data-sticky-label]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');

    this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.variants = this.readVariants();

    this.bindGallery();
    this.bindOptions();
    this.bindQuantity();
    this.bindForm();
    this.bindRoutineQuickAdd();
    this.bindStickyBar();

    const checkedVariantId = Number(this.variantInput?.value);
    const initialVariant = this.variants.find((variant) => Number(variant.id) === checkedVariantId);
    if (initialVariant) this.updateVariant(initialVariant, false);
  }

  readVariants() {
    const data = this.querySelector('[data-product-variants]');
    if (!data) return [];
    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      return [];
    }
  }

  bindGallery() {
    if (!this.slides.length) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.activateMediaByIndex(Number(thumb.dataset.galleryIndex));
      });
    });

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => {
      this.activateMediaByIndex(this.activeMediaIndex() - 1);
    });

    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => {
      this.activateMediaByIndex(this.activeMediaIndex() + 1);
    });
  }

  activeMediaIndex() {
    const current = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    return current >= 0 ? current : 0;
  }

  activateMediaByIndex(index) {
    if (!this.slides.length) return;
    const normalized = (index + this.slides.length) % this.slides.length;
    this.hydrateSlide(this.slides[normalized]);

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === normalized;
      slide.classList.toggle('is-active', active);
      slide.hidden = !active;
      if (!active) {
        slide.querySelectorAll('video').forEach((video) => video.pause());
      }
    });

    this.thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === normalized;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    const count = this.querySelector('[data-gallery-count]');
    if (count) count.textContent = `${normalized + 1} / ${this.slides.length}`;
  }

  hydrateSlide(slide) {
    if (!slide) return;
    slide.querySelectorAll('img[data-src]').forEach((image) => {
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.sizes) image.sizes = image.dataset.sizes;
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-sizes');
    });
  }

  activateMediaById(mediaId) {
    if (!mediaId) return;
    const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
    if (index >= 0) this.activateMediaByIndex(index);
  }

  bindOptions() {
    this.querySelectorAll('[data-option-position] input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => {
        const fieldset = input.closest('[data-option-position]');
        const selectedLabel = fieldset?.querySelector('[data-selected-option]');
        if (selectedLabel) selectedLabel.textContent = input.value;

        const selectedOptions = Array.from(this.querySelectorAll('[data-option-position]'))
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
          .map((group) => group.querySelector('input:checked')?.value);

        const variant = this.variants.find((candidate) =>
          candidate.options.every((option, optionIndex) => option === selectedOptions[optionIndex])
        );

        this.updateVariant(variant, true);
      });
    });
  }

  updateVariant(variant, updateUrl) {
    const available = Boolean(variant?.available);

    if (this.variantInput) this.variantInput.value = variant?.id || '';
    if (this.price && variant?.price) this.price.textContent = variant.price;
    if (this.stickyPrice && variant?.price) this.stickyPrice.textContent = variant.price;

    const hasComparePrice = Boolean(
      variant?.comparePrice &&
      Number(variant.comparePriceCents) > Number(variant.priceCents)
    );

    if (this.comparePrice) {
      this.comparePrice.textContent = hasComparePrice ? variant.comparePrice : '';
      this.comparePrice.classList.toggle('is-hidden', !hasComparePrice);
    }

    if (this.saving) {
      this.saving.textContent = hasComparePrice && variant.saving ? `Économisez ${variant.saving}` : '';
      this.saving.classList.toggle('is-hidden', !hasComparePrice);
    }

    if (this.availability) {
      this.availability.classList.toggle('is-unavailable', !available);
      const label = this.availability.querySelector('span');
      if (label) label.textContent = variant?.stockLabel || (available ? 'En stock' : 'Épuisé');
    }

    if (this.addButton) this.addButton.disabled = !available;
    if (this.addLabel) this.addLabel.textContent = available ? 'Ajouter au panier' : 'Indisponible';
    if (this.stickyButton) this.stickyButton.disabled = !available;
    if (this.stickyLabel) this.stickyLabel.textContent = available ? 'Ajouter au panier' : 'Indisponible';

    if (variant?.featuredMediaId) this.activateMediaById(variant.featuredMediaId);

    if (updateUrl && variant?.id && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  bindQuantity() {
    const input = this.querySelector('[data-quantity] input');
    if (!input) return;

    const update = (direction) => {
      const min = Number(input.min) || 1;
      const max = Number(input.max) || Infinity;
      const step = Number(input.step) || 1;
      const next = Math.min(max, Math.max(min, (Number(input.value) || min) + direction * step));
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => update(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => update(1));
  }

  bindForm() {
    if (!this.form) return;

    this.form.addEventListener('submit', async (event) => {
      if (!this.variantInput?.value || this.addButton?.disabled) {
        event.preventDefault();
        if (this.formStatus) this.formStatus.textContent = 'Choisissez une variante disponible.';
        return;
      }

      event.preventDefault();
      if (this.formStatus) this.formStatus.textContent = '';
      this.addButton?.classList.add('is-loading');
      if (this.addButton) this.addButton.disabled = true;
      if (this.stickyButton) this.stickyButton.disabled = true;
      if (this.addLabel) this.addLabel.textContent = 'Ajout en cours…';

      try {
        const cart = await this.fetchCart();
        const productId = Number(this.dataset.productId);
        const existingItem = cart.items?.find((item) => Number(item.product_id) === productId);

        if (existingItem) {
          if (this.addLabel) this.addLabel.textContent = 'Déjà dans le panier ✓';
          if (this.stickyLabel) this.stickyLabel.textContent = 'Déjà ajouté ✓';
          if (this.formStatus) {
            this.formStatus.textContent = Number(existingItem.variant_id) === Number(this.variantInput.value)
              ? 'Ce produit est déjà dans votre panier. Vous pourrez y ajuster sa quantité.'
              : 'Une autre variante de ce produit est déjà dans votre panier. Modifiez-la directement dans le panier.';
          }
          window.setTimeout(() => window.location.assign(this.dataset.cartUrl), 650);
          return;
        }

        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Impossible d’ajouter ce produit.');

        if (this.addLabel) this.addLabel.textContent = 'Ajouté au panier ✓';
        if (this.stickyLabel) this.stickyLabel.textContent = 'Ajouté ✓';
        window.setTimeout(() => window.location.assign(this.dataset.cartUrl), 420);
      } catch (error) {
        this.addButton?.classList.remove('is-loading');
        if (this.addButton) this.addButton.disabled = false;
        if (this.stickyButton) this.stickyButton.disabled = false;
        if (this.addLabel) this.addLabel.textContent = 'Ajouter au panier';
        if (this.formStatus) {
          this.formStatus.textContent = error instanceof Error
            ? error.message
            : 'Une erreur est survenue. Réessayez dans un instant.';
        }
      }
    });
  }

  fetchCart() {
    return fetch(`${this.dataset.cartUrl}.js`, {
      headers: { Accept: 'application/json' }
    }).then(async (response) => {
      if (!response.ok) throw new Error('Impossible de vérifier votre panier. Réessayez dans un instant.');
      try {
        return await response.json();
      } catch (error) {
        throw new Error('Impossible de vérifier votre panier. Réessayez dans un instant.');
      }
    });
  }

  updateCartCount(cart) {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(Number(cart.item_count || 0));
    });
  }

  bindRoutineQuickAdd() {
    this.addEventListener('change', (event) => {
      const select = event.target.closest('select[data-routine-quick-variant]');
      if (!select) return;
      const price = select.closest('[data-routine-product]')?.querySelector('[data-routine-quick-price]');
      const priceLabel = select.selectedOptions[0]?.dataset.priceLabel;
      if (price && priceLabel) price.textContent = priceLabel;
    });

    this.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-routine-quick-add]');
      if (!button || button.disabled) return;

      const card = button.closest('[data-routine-product]');
      const variantControl = card?.querySelector('[data-routine-quick-variant]');
      const status = card?.querySelector('[data-routine-quick-status]');
      const variantId = Number(variantControl?.value);
      const productId = Number(button.dataset.productId);
      const originalLabel = button.textContent;

      if (!variantId || !productId) return;
      button.disabled = true;
      button.textContent = 'Ajout…';
      if (status) status.textContent = '';

      try {
        const cart = await this.fetchCart();
        const alreadyPresent = cart.items?.some((item) => Number(item.product_id) === productId);
        if (alreadyPresent) {
          button.textContent = 'Déjà au panier ✓';
          if (status) status.textContent = 'Le produit déjà présent a été conservé, sans doublon.';
          return;
        }

        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Impossible d’ajouter ce produit.');

        const updatedCart = await this.fetchCart();
        this.updateCartCount(updatedCart);
        button.textContent = 'Ajouté ✓';
        if (status) status.textContent = 'Ajouté au panier. Vous pouvez poursuivre vos achats.';
      } catch (error) {
        button.disabled = false;
        button.textContent = originalLabel;
        if (status) {
          status.textContent = error instanceof Error
            ? error.message
            : 'L’ajout n’a pas abouti. Réessayez dans un instant.';
        }
      }
    });
  }

  bindStickyBar() {
    if (!this.sticky || !this.addButton) return;

    this.stickyButton?.addEventListener('click', () => {
      if (!this.stickyButton.disabled) this.form?.requestSubmit();
    });

    const setVisible = (visible) => {
      this.sticky.classList.toggle('is-visible', visible);
      this.sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => setVisible(!entry.isIntersecting),
        { threshold: 0.15 }
      );
      observer.observe(this.addButton);
    } else {
      window.addEventListener('scroll', () => {
        setVisible(this.addButton.getBoundingClientRect().bottom < 0);
      }, { passive: true });
    }
  }
}

if (!customElements.get('wody-product')) {
  customElements.define('wody-product', WodyProduct);
}
